import {Step, StepType} from "./step";

export enum UploadState {
  PENDING = 'PENDING',
  UPLOADING = 'UPLOADING',
  UPLOADED = 'UPLOADED',
  FAILED = 'FAILED'
}

export enum PictureKind {
  TYRE = 'TYRE',
  VIN = 'VIN',
  REGISTRATION_CARD = 'REGISTRATION_CARD'
}

export interface StoredStep {
  position: number;
  type: StepType;
}

// StoredPicture as interface with utility function because StoredPicture is
// the type used within indexedDB and structured clone delete object prototype.
export interface StoredPicture {
  key: string;
  kind: PictureKind;
  analysisId: number;
  vehicleId?: number;
  license?: string;
  capturedAt: number;
  uploadState: UploadState;
  uploadedAt?: number;
  attempts: number;
  lastError?: string;
  data: Uint8Array<ArrayBuffer>;
  step?: StoredStep;
}

// One week, in milliseconds.
export const ONE_WEEK_MS = 604_800_000;

const tyrePictureKey = (step: StoredStep): string => {
  return new Step(step.position, step.type).toString();
}

export const isTyre = (picture: StoredPicture): picture is StoredPicture & {step: StoredStep} => {
  return picture.kind === PictureKind.TYRE && !!picture.step;
}

export const isOlderThan = (picture: StoredPicture, ageMs: number, now = Date.now()): boolean => {
  return now - picture.capturedAt > ageMs;
}

export const pictureToFile = (picture: StoredPicture, fileName = 'image.jpg'): File => {
  return new File([picture.data], fileName, {type: 'image/jpeg'});
}

export interface PictureContext {
  analysisId: number;
  vehicleId?: number;
  license?: string;
}

const basePicture = (key: string, kind: PictureKind, data: Uint8Array<ArrayBuffer>, context: PictureContext): StoredPicture => {
  return {
    key,
    kind,
    analysisId: context.analysisId,
    vehicleId: context.vehicleId,
    license: context.license,
    capturedAt: Date.now(),
    uploadState: UploadState.PENDING,
    attempts: 0,
    data
  };
}

export const storedTyrePicture = (step: StoredStep, data: Uint8Array<ArrayBuffer>, context: PictureContext): StoredPicture => {
  const plainStep: StoredStep = {position: step.position, type: step.type};
  return {...basePicture(tyrePictureKey(plainStep), PictureKind.TYRE, data, context), step: plainStep};
}

// A single identification picture of a given kind is kept locally: a retake replaces the pending one.
const identificationPictureKey = (kind: PictureKind): string => kind;

export const storedIdentificationPicture = (kind: PictureKind, data: Uint8Array<ArrayBuffer>, context: PictureContext): StoredPicture => {
  return basePicture(identificationPictureKey(kind), kind, data, context);
}
